import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import api from '../services/api';
import config from '../config/config';
import { formatDate } from '../utils/dateUtils';
import DynamicTable from '../components/Documents/DynamicTable';

// Dynamic column and row interfaces based on API response
interface Column {
  class: string;
  header: string;
  key: string;
  show: number;
}

interface Row {
  [key: string]: any;
}

interface ApiResponse {
  columns: Column[];
  rows: Row[];
  document: {
    id: string;
    name: string;
    type: string;
    status: string;
    upload_date: string;
    page_count: number;
    presigned_url: string;
  };
}

const CashbookViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string>('');

  const handleExportExcel = async () => {
    if (!id) return;
    
    try {
      // Call the backend API to download Excel file
      const response = await api.post(`${config.apiBaseUrl}/data/download-cashbook`, {
        document_id: id
      }, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `cashbook_${apiData?.document.name.replace(/\.[^/.]+$/, '')}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  useEffect(() => {
    const fetchCashbookData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch cashbook details with dynamic columns
        const response = await api.get(`${config.apiBaseUrl}/data/cashbook-details/${id}`);
        setApiData(response.data);
        
        // Get presigned URL from the document data in the response
        if (response.data.document && response.data.document.presigned_url) {
          setPresignedUrl(response.data.document.presigned_url);
        }
      } catch (error) {
        console.error('Error fetching cashbook data:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCashbookData();
  }, [id]);

  // Handle document click (no drill-down for cashbook)
  const handleDocumentClick = (rowId: string, type: string) => {
    // No drill-down functionality for cashbook - do nothing
  };

  // Format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !apiData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-600 dark:text-red-400 mb-4">{error || 'No data available'}</div>
        <button
          onClick={() => navigate(-1)}
          className="text-primary hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { document } = apiData;
  
  if (document.status === 'Processing') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-semibold">Processing Document...</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              View All Documents
            </button>
          </div>
        </div>

        <div className="flex gap-4">
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'} flex items-center justify-center p-8`}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Processing your document. This may take a few minutes...</p>
            </div>
          </div>

          {showPreview && (
            <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="h-full">
                <iframe
                  src={presignedUrl}
                  className="w-full h-[calc(100vh-12rem)]"
                  title="PDF Preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-semibold">
            {apiData.rows && apiData.rows.length > 0 
              ? `Cashbook Entries - ${document.name}`
              : 'Cashbook Document'
            }
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleExportExcel}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            Export to Excel
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            View All Documents
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Table Section */}
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'}`}>
          <div className="p-6">
            {apiData.columns && apiData.rows && (
              <DynamicTable 
                columns={apiData.columns}
                rows={apiData.rows}
                selectedDocuments={[]}
                onSelectDocument={() => {}}
                onSelectAll={() => {}}
                onDocumentClick={handleDocumentClick}
                isRowExpanded={() => false}
                renderDrillDownContent={() => null}
                onDownloadExcel={() => {}}
                showDrillDown={false}
                showCheckboxes={false}
              />
            )}
          </div>
        </div>

        {/* PDF Preview Section */}
        {showPreview && (
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="h-full">
              <iframe
                src={presignedUrl}
                className="w-full h-[calc(100vh-12rem)]"
                title="PDF Preview"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashbookViewPage;
